import { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { DataGrid, GridRow } from '@mui/x-data-grid';
import { COLORS, DATE_FORMATS } from '../../../../utils/Application_Constants';
import formatDate, { formatUtcDate } from '../../../../utils/formatDate';
import { useInvoicingSelectionContext } from '../../../Contexts/InvoicingSelectionContext';
import { HIERARCHY_BORDER_STYLE } from '../styles';
import { decimalFormatter, currencyFormatter } from '../../../../utils/helpers';
import DataGridSortIcon from '../../../../Shared/Components/DataGridSortIcon';
import useDebounce from '../../../../utils/useDebounce';

const TABLE_BORDER_STYLE = `1px solid ${COLORS.LT_MIDNIGHT10}`;

const expenseItemColumns = [
    {
        field: 'transactionDate',
        headerName: 'Txn Date',
        flex: 1,
        sortable: true,
        valueFormatter: (value) => formatUtcDate(value),
    },
    {
        field: 'startDate',
        headerName: 'Start Date',
        flex: 1,
        sortable: true,
        valueFormatter: (value) => formatUtcDate(value),
    },
    {
        field: 'endDate',
        headerName: 'End Date',
        flex: 1,
        sortable: true,
        valueFormatter: (value) => formatUtcDate(value),
    },
    {
        field: 'type',
        headerName: 'Expense Type',
        flex: 1,
        sortable: false,
    },
    {
        field: 'billAmount',
        headerName: 'Bill Amount',
        flex: 1,
        sortable: false,
        valueFormatter: (value) => currencyFormatter(value),
    },
];

const createRowWithRef = (setRefsUpdated, lastChildRef, totalRows) => (props) => {
    const { rowId, index } = props;
    const rowRef = useRef(null);

    useEffect(() => {
        if (rowRef.current) {
            setRefsUpdated((prev) => {
                const newRefs = [...prev];

                const refIndex = newRefs.findIndex((ref) => ref.rowId === rowId);

                if (refIndex === -1) {
                    newRefs.push({ rowId, ref: rowRef.current });
                } else {
                    newRefs[refIndex] = { rowId, ref: rowRef.current };
                }

                return newRefs;
            });
        }

        return () => {
            setRefsUpdated((prev) => prev.filter((ref) => ref.rowId !== rowId));
        };
    }, [rowId, setRefsUpdated]);

    return (
        <GridRow
            ref={(ref) => {
                rowRef.current = ref;
                if (totalRows === index + 1) {
                    // eslint-disable-next-line no-param-reassign
                    lastChildRef.current = ref;
                }
            }}
            {...props}
        />
    );
};

const ExpenseEntriesCollapse = ({
    bookingId,
    expense,
    hierarchyConnectorVerticalRef,
    lastChildRef,
}) => {
    const { getSelectedSubItems, handleUpdateSubItemsSelection } = useInvoicingSelectionContext();
    const rowSelectionModel = useMemo(() => {
        const ids = getSelectedSubItems(bookingId, 'expense', expense.id);
        return { type: 'include', ids: new Set(ids) };
    }, [getSelectedSubItems, bookingId, expense.id]);
    const handleRowSelectionModelChange = useCallback((newSelectionModel) => {
        handleUpdateSubItemsSelection(bookingId, 'expense', expense.id, [...newSelectionModel.ids]);
    }, []);

    const dataGridRef = useRef(null);
    const gridHeaderRowRef = useRef(null);
    const [gridRowsRefs, setGridRowsRefs] = useState([]); // [{ rowId: number, ref: ref }]
    const [gridRowConnectorLines, setGridRowConnectorLines] = useState([]);

    // Update connector lines when grid rows refs change
    const updateConnectorLines = useCallback(() => {
        if (!hierarchyConnectorVerticalRef?.current || !dataGridRef?.current) return;

        const verticalConnectorRect = hierarchyConnectorVerticalRef.current.getBoundingClientRect();
        const dataGridRect = dataGridRef.current.getBoundingClientRect();

        const newConnectorLines = [];

        gridRowsRefs.forEach(({ rowId, ref }) => {
            if (ref) {
                const rowRect = ref.getBoundingClientRect();
                // Calculate positions relative to the DataGrid container
                const relativeTop = rowRect.top - dataGridRect.top;
                const left = verticalConnectorRect.left - rowRect.left;

                newConnectorLines.push({
                    id: rowId,
                    left: left + 1,
                    top: relativeTop + rowRect.height / 2,
                    width: -left,
                });
            }
        });

        // Update header connector line
        if (gridHeaderRowRef?.current) {
            const headerRect = gridHeaderRowRef.current.getBoundingClientRect();
            const relativeTop = headerRect.top - dataGridRect.top;
            const left = verticalConnectorRect.left - headerRect.left;
            const headerConnectorLine = {
                id: 'header',
                left: left + 1,
                width: -left,
                top: relativeTop + headerRect.height / 2,
            };
            newConnectorLines.push(headerConnectorLine);
        }

        setGridRowConnectorLines(newConnectorLines);
    }, [hierarchyConnectorVerticalRef, dataGridRef, gridRowsRefs, gridHeaderRowRef]);

    const debouncedUpdateConnectorLines = useDebounce(updateConnectorLines, 10);

    useEffect(() => {
        // Update on mount and window resize
        debouncedUpdateConnectorLines();

        const handleResize = () => {
            debouncedUpdateConnectorLines();
        };

        // Use ResizeObserver to watch for element size changes
        const resizeObserver = new ResizeObserver(handleResize);
        if (hierarchyConnectorVerticalRef?.current)
            resizeObserver.observe(hierarchyConnectorVerticalRef.current);
        if (dataGridRef?.current) resizeObserver.observe(dataGridRef.current);

        window.addEventListener('resize', handleResize);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, [debouncedUpdateConnectorLines]);

    const RowWithRefComponent = useMemo(
        () => createRowWithRef(setGridRowsRefs, lastChildRef, expense.items?.length),
        [expense.items?.length]
    );

    return (
        <Box position="relative" sx={{ width: '100%' }}>
            <DataGrid
                ref={dataGridRef}
                rowSelectionModel={rowSelectionModel}
                onRowSelectionModelChange={handleRowSelectionModelChange}
                sx={{
                    maxWidth: '800px',
                    borderRadius: '5px',
                    border: TABLE_BORDER_STYLE,
                    background: COLORS.WHITE,
                    '& .MuiDataGrid-iconSeparator': {
                        display: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        marginBottom: '0px',
                    },
                    '& .MuiDataGrid-row--borderBottom': {
                        '& .MuiDataGrid-columnHeader': {
                            borderBottom: 'none',
                        },
                    },
                    '& .MuiDataGrid-columnHeader': {
                        '& .MuiCheckbox-root': {
                            visibility: 'hidden', // hide the checkbox in the column header
                        },
                        backgroundColor: '#F4FAFF',
                        '&:nth-of-type(5),&:nth-of-type(9)': {
                            borderLeft: TABLE_BORDER_STYLE,
                        },
                    },
                    '& .MuiDataGrid-virtualScrollerContent': {
                        borderTop: 'none',
                    },
                    '& .MuiDataGrid-row': {
                        '--DataGrid-rowBorderColor': COLORS.LT_MIDNIGHT10,
                    },
                    '& .left-border': {
                        borderLeft: TABLE_BORDER_STYLE,
                    },
                    '& .bold-text': {
                        fontWeight: 'bold',
                    },
                }}
                autoHeight
                columnHeaderHeight={35}
                rowHeight={48}
                columns={expenseItemColumns}
                rows={expense.items}
                columnResizeIcon={false}
                disableAutosize
                disableColumnMenu
                disableColumnResize
                checkboxSelection
                disableSelectionOnClick
                disableRowSelectionOnClick
                slotProps={{
                    baseCheckbox: { tabIndex: 0 },
                    columnHeaders: { ref: gridHeaderRowRef },
                }}
                slots={{
                    columnHeaderSortIcon: DataGridSortIcon,
                    row: RowWithRefComponent,
                }}
            />

            {/* Horizontal connector lines */}
            {gridRowConnectorLines.map((line) => (
                <Box
                    key={line.id}
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        top: line.top,
                        left: line.left,
                        width: line.width,
                        height: 0,
                        borderTop: HIERARCHY_BORDER_STYLE,
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />
            ))}
        </Box>
    );
};

export default ExpenseEntriesCollapse;
